/**
 * An instance of this class is returned by the ContextManagerFactory's getInstance method. This should not be
 * instantiated directly since it is meant to be a singleton.
 * <br>
 * This extends dojo Stateful and hence has "get", "set" and "watch" methods in addition to the ones mentioned below.
 * "currentContext" and "secondaryContext" are the properties that should be set to drive the Context Manager.
 * They should be to objects of the following form:
 * <br>
 * {<br>
 *     appId: "myApp",<br>
 *     contextIdList: ["global", "context1", "context2"]<br>
 * }<br>
 * <br>
 * "global" is always the first member of the currentContext contextIdList. If it is omitted it will automatically added by the setter.
 * <br>
 * Also, if the currentContext is set but contextIdList is not provided, the setter will automatically make the
 * contextIdList: ["global"] in the currentContext.
 *
 * The secondaryContext differs from the primary or "currentContext" in terms of how it is treated by the individual contextual
 * feature managers.  For example the TabGroupManager remembers the tab selection associated with each currentContext but not so
 * with secondaryContext.  The QuickAccessActionManager first applies the currentContext and then the secondaryContext, meaning that
 * actions provide by the latter will take precedence over the former.  Hence, when the currentContext is the "Editor" and the secondary
 * context is the "Command Window" the QAB undo and redo buttons will operate on the Command Window.
 *
 * @extends dojo/Stateful
 * @module MW/uiframework/contextmanager/ContextManager
 *
 * @copyright Copyright 2013-2018 The MathWorks, Inc.
 */

define([
    "dojo/_base/declare",
    "dojo/Stateful",
    "./PanelManager",
    "./QuickAccessActionManager",
    "./StatusComponentManager",
    "./TabGroupManager",
    "MW/uiframework/UIBuilderFactory"
], function (declare, Stateful, PanelManager, QuickAccessActionManager, StatusComponentManager, TabGroupManager, UIBuilderFactory) {
    return declare([Stateful], {
        currentContext: null,
        secondaryContext: null,

        constructor: function (args) {
            this.parentAppId = args.parentAppId;
            this.alternativeContextMemory = !!args.alternativeContextMemory;
            this._uiBuilder = args.uiBuilder || UIBuilderFactory.getInstance();
            this.toolstripId = this._uiBuilder.tagToId(args.toolstripTag);
            if (!this.toolstripId) {
                throw new Error("No toolstrip with tag \"" + args.toolstripTag + "\" exists!");
            }

            this._panelManager = new PanelManager({
                contextManagerImpl: this,
                uiContainer: args.uiContainer
            });

            this._quickAccessActionManager = new QuickAccessActionManager({
                contextManagerImpl: this,
                uiBuilder: this._uiBuilder,
                qabContextualControlsActionMap: args.qabContextualControlsActionMap
            });

            this._statusComponentManager = new StatusComponentManager({
                contextManagerImpl: this,
                uiContainer: args.uiContainer
            });

            this._tabGroupManager = new TabGroupManager({
                contextManagerImpl: this,
                uiBuilder: this._uiBuilder
            });
        },


        /**
         * This method is used to register TabGroups, QuickAccessActions, Panels and StatusComponents for individual contexts
         * @param {String} what - This can be "TabGroups", "QuickAccessAction", "Panels" or "StatusComponents", depending
         * on what you want to register
         * @param {Object} args - This should contain "appId", "contextId" and one of the following:
         * 1. "tabGroupTags" - when registering "TabGroups"
         * 2. "quickAccessControlTag" and "actionId" - when registering a "QuickAccessAction"
         * 3. "panelIds" when registering "Panels"
         * 4. "statusComponentTags" - when registering "StatusComponents"
         * @param {String} args.appId - This is the appId of the app for whose context you want to register
         * @param {String} args.contextId - This is the contextId for which you want to register
         * @param {String[]} args.tabGroupTags - An array of tags of the tab groups that you want to register with the
         * supplied appId and contextId
         * @param {String} args.quickAccessControlTag - The tag of the quickAccess control for which you want to
         * register an actionId for the supplied appId and contextId
         * @param {String} args.actionId - The id of the action that you want to register for the supplied appId,
         * contextId and quickAccessControlTag
         * @param {String[]} args.panelIds - An array of panelIds that you want to register with the supplied appId
         * and contextId
         * @param {String[]} args.statusComponentTags - An array tags identifying the status components that you want
         * to register with the supplied appId and contextId
         * @instance
         */
        register: function (what, args) {
            switch (what) {
            case "TabGroups":
                this._tabGroupManager.registerTabGroups(args);
                break;
            case "QuickAccessAction":
                this._quickAccessActionManager.registerQuickAccessAction(args);
                break;
            case "Panels":
                this._panelManager.registerPanels(args);
                break;
            case "StatusComponents":
                this._statusComponentManager.registerComponents(args);
                break;
            default:
                throw new Error("This is not a valid type to register!");
            }
        },

        /**
         * This method is used to unregister TabGroups, QuickAccessActions, Panels or StatusComponents for individual contexts
         * @param {String} what - This can be "TabGroups", "QuickAccessAction", "Panels" or "StatusComponents" depending
         * on what you want to unregister
         * @param {Object} args - This should contain "appId", "contextId" when unregistering "TabGroups", "Panels" or "StatusComponents".
         * Additionally it should contain "quickAccessControlTag" when unregistering a "QuickAccessAction".
         * @param {String} args.appId - This is the appId of the app for whose context you want to unregister
         * @param {String} args.contextId - This is the contextId for which you want to unregister
         * @param {String} args.quickAccessControlTag - The tag of the quickAccess control that you want to unregister
         * for the supplied appId and contextId
         * @instance
         */
        unregister: function (what, args) {
            switch (what) {
            case "TabGroups":
                this._tabGroupManager.unregisterTabGroups(args);
                break;
            case "QuickAccessAction":
                this._quickAccessActionManager.unregisterQuickAccessAction(args);
                break;
            case "Panels":
                this._panelManager.unregisterPanels(args);
                break;
            case "StatusComponents":
                this._statusComponentManager.unregisterComponents(args);
                break;
            default:
                throw new Error("This is not a valid type to unregister!");
            }
        },

        /**
         * Each context remembers the last selected tab and switches back to it based on certain rules the next time it
         * comes into context. This method can be used to reset that remembered last selected tab.
         * @param {Object} args - This should contain "appId" and "contextId"
         * @param {String} args.appId - This is the appId of the app for whose context's last selected tab you want to
         * reset
         * @param {String} args.contextId - This is the contextId whose last selected tab you want to reset.
         * @instance
         */
        resetLastSelectedTab: function (args) {
            this._tabGroupManager.resetLastSelectedTab(args);
        },

        /**
         * This method cleans up the Context Manager.
         * @instance
         */
        cleanup: function () {
            this._quickAccessActionManager.cleanup();
            this._tabGroupManager.cleanup();
            this._panelManager.cleanup();
            this._statusComponentManager.cleanup();
        },

        /**
         * This is called every time someone sets the "context".
         * @param value
         * @private
         */
        _currentContextSetter: function (value) {
            if (value && value.appId) {
                // If the contextIdList is not provided, then make it ["global"]
                if (!value.contextIdList || value.contextIdList.length === 0) {
                    value.contextIdList = ["global"];
                    // If the given contextIdList doesn't have "global" already then make it the first one
                } else if (value.contextIdList.length > 0 && value.contextIdList.indexOf("global") === -1) {
                    value.contextIdList.splice(0, 0, "global");
                }
            }

            this.currentContext = value;
        }
    });
});